import { useAuthContext } from '@/auth/useAuthContext';
import { getTeam, getTransportManifest, listVehicles, updateTransportAssignment } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { TeamMember, TransportAssignment, TransportStatus, Vehicle } from '@/lib/types';
import { formatWhat3Words, what3wordsMapUrl } from '@/lib/what3words';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Truck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayLocalIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function shiftDate(dateIso: string, days: number): string {
  const d = new Date(`${dateIso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function timeLabel(scheduledAt: string): string {
  // scheduledAt is stored as ISO text; render the wall-clock portion.
  const t = scheduledAt.includes('T') ? scheduledAt.split('T')[1] : '';
  return t ? t.slice(0, 5) : '—';
}

function statusMeta(status: TransportStatus): { label: string; className: string } {
  switch (status) {
    case 'scheduled':
      return { label: 'Scheduled', className: 'bg-primary/10 text-primary ring-hairline' };
    case 'in_progress':
      return { label: 'On the road', className: 'bg-warning/10 text-warning ring-hairline' };
    case 'completed':
      return { label: 'Completed', className: 'bg-success/10 text-success ring-hairline' };
    case 'cancelled':
      return {
        label: 'Cancelled',
        className: 'bg-secondary text-muted-foreground ring-hairline',
      };
  }
}

function StatusBadge({ status }: { status: TransportStatus }) {
  const { label, className } = statusMeta(status);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}

function DirectionBadge({ direction }: { direction: 'pickup' | 'dropoff' }) {
  const isPickup = direction === 'pickup';
  const Icon = isPickup ? ArrowUpFromLine : ArrowDownToLine;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground ring-hairline">
      <Icon className="size-3" strokeWidth={1.75} />
      {isPickup ? 'Pickup' : 'Dropoff'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TransportPage() {
  const { state } = useAuthContext();
  const capabilities: readonly string[] = state.me?.capabilities ?? [];
  const canConfigure = capabilities.includes('*') || capabilities.includes('transport.configure');
  const currency = state.me?.activeAccount?.currency ?? 'GBP';

  const [date, setDate] = useState(todayLocalIso());
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [assignments, setAssignments] = useState<TransportAssignment[] | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await getTransportManifest(date, vehicleFilter || undefined);
      setAssignments(res.assignments);
    } catch (e) {
      setError((e as Error).message || 'Failed to load the transport manifest');
      setAssignments([]);
    }
  }, [date, vehicleFilter]);

  useEffect(() => {
    setAssignments(null);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    listVehicles()
      .then((res) => setVehicles(res.vehicles))
      .catch(() => setVehicles([]));
    getTeam()
      .then((members) => setTeam(members.filter((m) => m.status === 'active')))
      .catch(() => setTeam([]));
  }, []);

  const vehicleName = useMemo(() => {
    const map = new Map(vehicles.map((v) => [v.id, v.name]));
    return (id: string | null) => (id ? (map.get(id) ?? 'Unknown vehicle') : null);
  }, [vehicles]);

  const driverName = useMemo(() => {
    const map = new Map(team.map((m) => [m.id, m.userName ?? m.userEmail]));
    return (id: string | null) => (id ? (map.get(id) ?? 'Unknown driver') : null);
  }, [team]);

  async function patch(
    id: string,
    input: Parameters<typeof updateTransportAssignment>[1],
  ): Promise<void> {
    setBusyId(id);
    try {
      await updateTransportAssignment(id, input);
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  }

  const loading = assignments === null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-semibold">Transport</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Daily pickup and dropoff manifest — assign vehicles and drivers, track runs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Previous day"
            className="press rounded-lg border border-border bg-surface p-2 hover:bg-secondary"
            onClick={() => setDate((d) => shiftDate(d, -1))}
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} />
          </button>
          <input
            type="date"
            aria-label="Manifest date"
            value={date}
            onChange={(e) => setDate(e.target.value || todayLocalIso())}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            aria-label="Next day"
            className="press rounded-lg border border-border bg-surface p-2 hover:bg-secondary"
            onClick={() => setDate((d) => shiftDate(d, 1))}
          >
            <ChevronRight className="size-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className="press rounded-lg border border-border bg-surface px-3 py-1.5 text-sm hover:bg-secondary"
            onClick={() => setDate(todayLocalIso())}
          >
            Today
          </button>
        </div>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label htmlFor="transport-vehicle-filter" className="text-sm text-muted-foreground">
          Vehicle
        </label>
        <select
          id="transport-vehicle-filter"
          value={vehicleFilter}
          onChange={(e) => setVehicleFilter(e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
        >
          <option value="">All vehicles</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        {canConfigure && (
          <Link
            to="/settings/vehicles"
            className="ml-auto text-sm text-primary underline-offset-2 hover:underline"
          >
            Manage vehicles
          </Link>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-6 space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : assignments.length === 0 ? (
        <div className="mt-10 flex flex-col items-center gap-2 py-10 text-center">
          <Truck className="size-8 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium">No transport runs on this day</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Pickups and dropoffs added during booking will appear here. You can also add transport
            to any booking from its detail page.
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3">
          {assignments.map((a) => (
            <li key={a.id} className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="num min-w-[3.25rem] text-lg font-semibold tabular-nums">
                    {timeLabel(a.scheduledAt)}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <DirectionBadge direction={a.direction} />
                      <StatusBadge status={a.status} />
                      {a.feeMinor > 0 && (
                        <span className="num text-xs text-muted-foreground">
                          {formatPriceMinor(a.feeMinor, currency)}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium">
                      {a.petName ?? 'Pet'}
                      {a.customerName ? (
                        <span className="text-muted-foreground"> · {a.customerName}</span>
                      ) : null}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {a.pickupAddress && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="size-3.5" strokeWidth={1.75} />
                          {a.pickupAddress}
                        </span>
                      )}
                      {a.pickupWhat3words && (
                        <a
                          href={what3wordsMapUrl(a.pickupWhat3words)}
                          target="_blank"
                          rel="noreferrer"
                          className="num inline-flex items-center gap-1 font-medium text-foreground hover:underline"
                          title="Open the exact square in what3words"
                        >
                          {formatWhat3Words(a.pickupWhat3words)}
                        </a>
                      )}
                      {a.customerPhone && (
                        <a
                          href={`tel:${a.customerPhone}`}
                          className="num inline-flex items-center gap-1 hover:text-foreground"
                        >
                          <Phone className="size-3.5" strokeWidth={1.75} />
                          {a.customerPhone}
                        </a>
                      )}
                      {a.notes && <span>{a.notes}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {canConfigure ? (
                    <div className="flex items-center gap-2">
                      <select
                        aria-label="Assign vehicle"
                        value={a.vehicleId ?? ''}
                        disabled={busyId === a.id}
                        onChange={(e) => void patch(a.id, { vehicleId: e.target.value || null })}
                        className="rounded-lg border border-border bg-surface px-2 py-1 text-xs"
                      >
                        <option value="">No vehicle</option>
                        {vehicles.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Assign driver"
                        value={a.driverMembershipId ?? ''}
                        disabled={busyId === a.id}
                        onChange={(e) =>
                          void patch(a.id, { driverMembershipId: e.target.value || null })
                        }
                        className="rounded-lg border border-border bg-surface px-2 py-1 text-xs"
                      >
                        <option value="">No driver</option>
                        {team.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.userName ?? m.userEmail}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {[vehicleName(a.vehicleId), driverName(a.driverMembershipId)]
                        .filter(Boolean)
                        .join(' · ') || 'Unassigned'}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    {a.status === 'scheduled' && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void patch(a.id, { status: 'in_progress' })}
                        className="press rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                      >
                        Start run
                      </button>
                    )}
                    {a.status === 'in_progress' && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void patch(a.id, { status: 'completed' })}
                        className="press rounded-lg bg-success px-3 py-1.5 text-xs font-medium text-white hover:bg-success/90 disabled:opacity-50"
                      >
                        Mark completed
                      </button>
                    )}
                    {(a.status === 'scheduled' || a.status === 'in_progress') && (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => void patch(a.id, { status: 'cancelled' })}
                        className="press rounded-lg border border-border bg-surface px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
